import type { HelpTextProps } from '@react-types/shared/src/inputs';
import type { ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import React from 'react';
import { mergeProps, useField } from 'react-aria';

import type { FieldPropsWithElement } from './common/types';
import { Alert } from '../../components/Alert/Alert';
import { Badge } from '../../components/Badge/Badge';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { styled } from '../../stitches.config';
import { VStack } from '../../utilities/Stack/VStack';
import {
  type AccessLevel,
  type AccessLevelProp,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import filterErrorMessage from '../../utils/filterErrorMessage';
import { ControlProvider } from '../shared/context';
import { ErrorMessages, useErrorMessages } from './common/ErrorMessageRegistry';
import { FieldCompositeProvider } from './common/FieldCompositeProvider';
import { FieldDivider, fieldDividerDisplay } from './common/FieldDivider';
import { PrimaryFieldDescription, PrimaryFieldLabel } from './common/FieldLabel';
import { useDescriptionAndErrorIds } from './common/useDescriptionAndErrorIds';
import { useCustomFieldProps } from './createCustomFieldPropsProvider';
import { MaybeFieldContainer } from './FieldContainer';
import { FieldErrorMessages } from './FieldError';

const PrimaryFieldDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});
PrimaryFieldDetails.displayName = 'PrimaryFieldDetails';

const PrimaryFieldHeader = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
});
PrimaryFieldHeader.displayName = 'PrimaryFieldHeader';

const PrimaryFieldBadges = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
PrimaryFieldBadges.displayName = 'PrimaryFieldBadges';

const PrimaryFieldLabelling = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$6',
});
PrimaryFieldLabelling.displayName = 'PrimaryFieldLabelling';

const PrimaryFieldControls = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '$6',
});
PrimaryFieldControls.displayName = 'PrimaryFieldControls';

const PrimaryFieldAlert = styled(Alert, {
  padding: '$8 $12',
});
PrimaryFieldAlert.displayName = 'PrimaryFieldAlert';

const PrimaryFieldContainer = styled('div', {
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '$12 $16',
  [fieldDividerDisplay]: 'none',

  '&:not(:last-child)': {
    paddingBottom: 0,
    [fieldDividerDisplay]: 'flex',
  },
});
PrimaryFieldContainer.displayName = 'PrimaryFieldContainer';

export interface PrimaryFieldProps extends FieldPropsWithElement {
  accessLevel?: AccessLevelProp;
  badges?: React.ReactNode;
  controls?: React.ReactNode;
  children?: React.ReactNode;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  /**
   * Marks the field as optional with a badge.
   */
  optional?: boolean;
}

export function PrimaryField(props: PrimaryFieldProps) {
  const { fieldProps: customFieldProps, inputProps: customInputProps } = useCustomFieldProps();

  const {
    accessLevel: accessLevelProp,
    badges,
    label = customFieldProps.label,
    description = customFieldProps.description,
    errorMessage = customFieldProps.errorMessage,
    controls,
    element,
    children,
    internal,
    optional,
  } = props;

  const {
    labelProps,
    fieldProps: inputProps,
    descriptionProps,
    errorMessageProps,
  } = useField({
    label,
    description,
    errorMessage,
  });

  const accessLevel = useAccessLevelContext(accessLevelProp as AccessLevel);

  return (
    <MaybeFieldContainer>
      <PrimaryFieldContainer>
        <VStack spacing={8}>
          <PrimaryFieldDetails>
            <PrimaryFieldHeader>
              <PrimaryFieldLabelling>
                <PrimaryFieldLabel {...labelProps}>{label}</PrimaryFieldLabel>
                {optional && (
                  <Badge ends="card" size="small" variant="neutral">
                    Optional
                  </Badge>
                )}
                {accessLevel ? (
                  <FeatureBadge type={accessLevel} size="x-small" />
                ) : (
                  internal && <FeatureBadge type="internal" size="x-small" />
                )}
                {badges && (
                  <PrimaryFieldBadges>
                    <ControlProvider value={{ size: 'small' }}>{badges}</ControlProvider>
                  </PrimaryFieldBadges>
                )}
              </PrimaryFieldLabelling>
              {controls && (
                <PrimaryFieldControls>
                  <ControlProvider value={{ size: 'small' }}>{controls}</ControlProvider>
                </PrimaryFieldControls>
              )}
            </PrimaryFieldHeader>
            {description && (
              <PrimaryFieldDescription {...descriptionProps}>{description}</PrimaryFieldDescription>
            )}
          </PrimaryFieldDetails>
          <ControlProvider value={{ size: 'medium' }}>
            <Slot {...mergeProps(customInputProps, inputProps)}>{element}</Slot>
            {children}
          </ControlProvider>
        </VStack>
        <FieldDivider context="primary" />
      </PrimaryFieldContainer>
      {errorMessage && (
        <FieldErrorMessages>
          <PrimaryFieldAlert
            {...errorMessageProps}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        </FieldErrorMessages>
      )}
    </MaybeFieldContainer>
  );
}

export type PrimaryFieldCompositeProps = {
  badges?: ReactNode;
  controls?: ReactNode;
  fields: ReactNode;
  accessLevel?: AccessLevelProp;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: boolean;
  label: ReactNode;
  /**
   * Marks the field as optional with a badge.
   */
  optional?: boolean;
  /**
   * If true, multiple errors with the same message will not be shown.
   */
  deduplicateErrors?: boolean;
} & HelpTextProps;

export function PrimaryFieldComposite({
  badges,
  controls,
  description,
  errorMessage,
  fields,
  accessLevel: accessLevelProp,
  internal,
  label,
  optional,
  deduplicateErrors,
}: PrimaryFieldCompositeProps) {
  const { messages, registerMessage, unregisterMessage } = useErrorMessages();

  const { descriptionId, errorMessageId, describedBy } = useDescriptionAndErrorIds(
    filterErrorMessage(errorMessage),
    description,
  );

  const accessLevel = useAccessLevelContext(accessLevelProp as AccessLevel);

  return (
    <FieldCompositeProvider
      registerMessage={registerMessage}
      unregisterMessage={unregisterMessage}
      describedBy={describedBy}
    >
      <MaybeFieldContainer>
        <PrimaryFieldContainer>
          <VStack spacing={8}>
            <PrimaryFieldDetails>
              <PrimaryFieldHeader>
                <PrimaryFieldLabelling>
                  <PrimaryFieldLabel>{label}</PrimaryFieldLabel>
                  {optional && (
                    <Badge ends="card" size="small" variant="neutral">
                      Optional
                    </Badge>
                  )}
                  {accessLevel
                    ? isAccessLevelVisible(accessLevel) && <FeatureBadge type={accessLevel} />
                    : internal && <FeatureBadge type="internal" />}
                  {badges && (
                    <PrimaryFieldBadges>
                      <ControlProvider value={{ size: 'small' }}>{badges}</ControlProvider>
                    </PrimaryFieldBadges>
                  )}
                </PrimaryFieldLabelling>
                {controls && (
                  <PrimaryFieldControls>
                    <ControlProvider value={{ size: 'small' }}>{controls}</ControlProvider>
                  </PrimaryFieldControls>
                )}
              </PrimaryFieldHeader>
              {description && (
                <PrimaryFieldDescription id={descriptionId}>{description}</PrimaryFieldDescription>
              )}
            </PrimaryFieldDetails>
            <ControlProvider value={{ size: 'medium' }}>{fields}</ControlProvider>
          </VStack>
          <FieldDivider context="primary" />
        </PrimaryFieldContainer>
        <ErrorMessages messages={messages} deduplicate={deduplicateErrors} />
        {errorMessage && (
          <PrimaryFieldAlert
            id={errorMessageId}
            type="inline"
            relation="stacked"
            variant="negative"
            heading={filterErrorMessage(errorMessage)}
          />
        )}
      </MaybeFieldContainer>
    </FieldCompositeProvider>
  );
}
